/**
 * Rules shared by the in-page tab bars (`ecpl-tab-navigation`) and the
 * hover sub-navigation in the left navigation bar. Both views call these,
 * so a tab cannot appear in one and not the other.
 */

import { FeatureKey } from '../../_models/feature';

/** Tab links whose route never carries the parent id. */
const PARENTLESS_TAB_LINKS = ['sickness/list', 'course/list', 'list'];

/**
 * Sub-features hidden on every business and application, whatever the feature
 * JSON says. Hidden, not removed: the nodes stay in the stored JSON, so the
 * admin feature tree must not render them either — saving a tree without them
 * would delete them from the business.
 *
 * Flexi's OHS-plan (62), Additional Modules (214) and Additional Services (215)
 * tabs are retired on every application. The data is removed by
 * Database/Flexi/20261013_1450_0000_RemoveOhsPlanAndAddOnTabsFromFlexi.sql;
 * hiding them here as well keeps them out of sight in any database that has
 * not run it yet.
 */
const HIDDEN_SUB_FEATURE_IDS: number[] = [
  FeatureKey.FLEXI_OHS_PLAN,
  212, // Retired Additional Resources tab.
  FeatureKey.FLEXI_ADDITIONAL_MODULES,
  FeatureKey.FLEXI_ADDITIONAL_SERVICES
];

/**
 * Whether a sub-feature is hidden everywhere — tab bars, the hover
 * sub-navigation and the admin feature tree.
 * @param id The sub-feature's id from the feature JSON.
 * @param linkText The route identifies legacy Safety Rounds entries whose id
 * overlaps with an unrelated dashboard feature.
 */
export function isHiddenSubFeature(id: any, linkText?: string): boolean {
  return HIDDEN_SUB_FEATURE_IDS.indexOf(+id) > -1 ||
    (linkText === 'safety-rounds' && +id !== FeatureKey.FLEXI_SAFETY_ROUNDS);
}

/**
 * Whether a sub-feature is shown as a tab: not hidden globally, enabled for the
 * business (or on by default) and flagged for navigation (`IsFeature`
 * contains `N`).
 * @param child Sub-feature node from the feature JSON.
 */
export function isNavigableSubFeature(child: any): boolean {
  return (
    !isHiddenSubFeature(child?.id, child?.linkText) &&
    !!(child?.checked || child?.IsDefault) &&
    `${child?.IsFeature ?? ''}`.indexOf('N') > -1
  );
}

/** Flexi selections are explicit: a default must not override a disabled business or portal tab. */
export function isNavigableFlexiTab(child: any): boolean {
  return (
    isNavigableSubFeature(child) &&
    child.checked !== false &&
    child.access !== false
  );
}

/**
 * Router commands for a tab, relative to the module route that hosts the tab bar.
 * @param linkText The tab's `linkText`.
 * @param parentId Feature or record id passed to the tab.
 * @param parentKey Set when the parent id is a route segment rather than a matrix parameter.
 */
export function tabRouteCommands(
  linkText: string,
  parentId: number,
  parentKey?: string
): any[] {
  const id = PARENTLESS_TAB_LINKS.indexOf(linkText) > -1 ? 0 : parentId;
  if (id > 0 && parentKey) {
    return [linkText, id];
  }
  return [linkText, { Id: id }];
}

/**
 * Whether the user's rights allow a tab. Tabs without a rights entry are allowed.
 * @param rights Rights per tab id, or nothing when the page applies none.
 * @param tabId The tab's feature id.
 */
export function isTabAllowed(
  rights: { id: number; access: boolean }[] | null | undefined,
  tabId: number
): boolean {
  const entry = rights?.find(x => x.id === tabId);
  return entry ? entry.access : true;
}
